// April 2026 monthly report companion blog.
import { defineDocument, em, link, p, section, strong, text, } from '../../schema';
const aprilReportHref = '/party-central/reports/2026-04-top-party-games-and-streamers';
export const blogPartyGameStreamingByTheNumbersApril2026Document = defineDocument({
    id: 'blog:party-game-streaming-by-the-numbers-april-2026',
    kind: 'blog',
    slug: 'party-game-streaming-by-the-numbers-april-2026',
    route: '/blog/party-game-streaming-by-the-numbers-april-2026',
    seo: {
        title: 'Party Game Streaming by the Numbers: April 2026',
        description: 'nanos world topped Party Score, Lethal Company won raw volume, and KYR_SP33DY led the April 2026 streamer rankings.',
        imageAsset: 'twitchStats202604',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Game Streaming by the Numbers: April 2026')],
        description: [
            text('Lethal Company rises on new release, nanos world tops Party Score, and KYR_SP33DY leads the streamer rankings.'),
        ],
        imageAsset: 'twitchStats202604',
        imageAlt: 'Dynamic collage of top games and streamers for April 2026',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-05-12T00:00:00.000Z'),
        updatedAt: new Date('2026-05-12T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('Although April stream volume cooled from March, there were some amazing stories that emerged from the data. Across the month, we observed nearly '),
                    strong('190,000 party game streams'),
                    text(' from '),
                    strong('80,000 streamers'),
                    text(' covering '),
                    strong('1,000 games'),
                    text(' in '),
                    strong('35 languages'),
                    text('. Surprise hits moved the needle, big releases gave old games new life, and top streamers brought attention to new titles.'),
                ]),
                p([
                    text('The full April 2026 party game streaming report '),
                    link(aprilReportHref, 'is now available.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__its-nanos-world-and-were-just-living-in-it',
                    title: `It's Nanos World, and We're Just Living in It`,
                    blocks: [
                        p([
                            text('Seemingly out of nowhere, '),
                            em('nanos world'),
                            text(' showed up as the #1 game by Party Score in April. The game jumped from only 157 streams in March to 2,127 in April, with most of that activity coming from French-language channels playing '),
                            em('Zenkai'),
                            text(', a ninja role-playing game built on the '),
                            em('nanos world'),
                            text(' platform. Players of the game came back religiously—we spotted the average streamer more than 10 times during April—and they brought a big audience with them, too.'),
                        ]),
                        p([
                            text('That is exactly the kind of hot game Party Score is meant to catch: a game does not have to be the biggest title on Twitch to be thriving inside party-game streaming. Not only did the concentrated community attention boost this game to the top of the rankings, but we saw a revival of French streaming in general. In a month where party game streaming was down overall, French streams and viewership surged, pushing up to #3 in total stream volume by language. It goes to show how one major hit can ripple across the entire ecosystem.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__the-blooming-update',
                    title: 'The Blooming Update',
                    blocks: [
                        p([
                            em('Lethal Company'),
                            text(' had a stellar month with the release of Version 80, known as the "Blooming Update," driving it to the top of the Most Streamed Games list. The update introduced significant new content after a long hiatus, including three new creatures, a "March" moon rework, new factory interior rooms, and a new utility slot. With a 94% month-over-month increase in unique streamers, it even surpassed chart-toppers like '),
                            em('Among Us'),
                            text(', '),
                            em('Fall Guys'),
                            text(', and '),
                            em('Super Battle Golf'),
                            text(' in total number of streams.'),
                        ]),
                        p([
                            text('On the flip side, '),
                            em('Diablo II: Resurrected'),
                            text(` continues its slide from last month, falling to the #4 game by Party Score and #6 by stream volume. It's the second month in a row we're seeing cracks in its popularity and stickiness among streamers. `),
                            em('Words on Stream'),
                            text(' and '),
                            em('Gartic on Stream'),
                            text(` both continue their hot streak as some of the industry's Stickiest Games.`),
                        ]),
                        p([
                            text('Billiards also had a moment in April, with two games making the list of Games with Biggest Audiences. Large streamers also brought attention to titles like '),
                            em('Pixoguess'),
                            text(' and '),
                            em('Make Way'),
                            text(', which outperformed their typical popularity during the month.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__creator-leaderboard-rewarded-range',
                    title: 'KYR_SP33DY Steals the Show',
                    blocks: [
                        p([
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(' led April streamers with a 98 Party Score. He was able to outshine other streamers with more than a dozen appearances across 10 different party games and an average audience of 1,264 viewers. While four other streamers achieved a similar score through consistency, nobody had the combination of variety and audience size that KYR_SP33DY did.'),
                        ]),
                        p([
                            link('https://twitch.tv/SliverRK', 'SliverRK'),
                            text(' had an exciting month as the #3 Streamer by Party Score and the #1 Most Active streamer of '),
                            em('Mario Kart World'),
                            text(', the #3 game by Party Score. '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(' rose to the #9 spot on the Party Score leaderboard and was ranked as the #2 Most Versatile Streamer. Familiar faces like '),
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(' and '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' landed again on the Streamers with Biggest Audiences list this month. '),
                        ]),
                    ],
                }),
                section({
                    id: 'article__flock-around-and-find-out',
                    title: 'Flock Around and Find Out',
                    blocks: [
                        p([
                            text('April had 37 new party games released. Ruling the roost was '),
                            link('https://store.steampowered.com/app/3618030/Flock_Around/', 'Flock Around'),
                            text(', which launched April 24 and still reached nearly 300 streamers before the month ended. '),
                            em('Flock Around'),
                            text(' is a co-op birdwatching game where you explore a nature sanctuary and try to snap pictures of birds.'),
                        ]),
                        p([
                            text('Another new release to keep an eye on is '),
                            em("Don't Lose Aggro"),
                            text('. Released on April 15, it had a strong start thanks to '),
                            link('https://twitch.tv/sasatikk', 'sasatikk'),
                            text(' bringing more than 5,000 viewers to the title. '),
                        ]),
                    ],
                }),
                section({
                    id: 'article__read-the-full-report',
                    title: 'Read the Full Report',
                    blocks: [
                        p([
                            text('In the '),
                            link(aprilReportHref, 'full report'),
                            text(' we dive deep into the data for the top games and streamers of April. There you will find complete Party Score rankings, game analysis, new releases, streamer spotlights, language breakdowns, and streaming patterns for the month.'),
                        ]),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=party-game-streaming-by-the-numbers-april-2026.js.map